import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RetailersService, RetailerResponse, ShipmentResponse, ShipmentsService, ShipmentRequest, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-shipment',
    templateUrl: './edit-shipment.component.html',
    styleUrl: './edit-shipment.component.scss',
    host: {
        class: 'app-edit-shipment'
    },
    standalone: false
})
export class EditShipmentComponent extends BaseViewComponent implements OnInit {
  shipmentId: number = 0;
  shipment: ShipmentResponse = {};

  filterOperator = FilterOperator;
  storeData: {
    retailer: { list: RetailerResponse[]; totalRecords: number; rowsPerPage: number };
    origin: { list: RetailerResponse[]; totalRecords: number; rowsPerPage: number };
    destination: { list: RetailerResponse[]; totalRecords: number; rowsPerPage: number };
  } = {
      retailer: { list: [], totalRecords: 0, rowsPerPage: 10 },
      origin: { list: [], totalRecords: 0, rowsPerPage: 10 },
      destination: { list: [], totalRecords: 0, rowsPerPage: 10 }
    };

  public shipmentForm: FormGroup = this.fb.group({
    reference: [null, [Validators.required, Validators.maxLength(50)]],
    retailer: [[], [Validators.required]],
    origin: [[], [Validators.required]],
    destination: [[], [Validators.required]],
    comment: [null, [Validators.maxLength(255)]]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private retailersService: RetailersService,
    private shipmentsService: ShipmentsService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.shipmentId = params['shipmentId'];

      if (!this.shipmentId) {
        return;
      }

      this.shipment = await firstValueFrom(
        this.shipmentsService.apiShipmentsIdGet(this.shipmentId)
      );

      await Promise.all([
        this.loadStores('retailer'),
        this.loadStores('origin'),
        this.loadStores('destination', null),
      ]);

      await this.initForm();
    });
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  async loadStores(storeType: 'retailer' | 'origin' | 'destination', event: any = null) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.storeData[storeType].rowsPerPage
    }
    if (search) {
      payload.textFilter = search;
    }

    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });

    payload.additionalFilters = filters;

    const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));

    this.storeData[storeType].list = storesPage.results ?? [];
    this.storeData[storeType].totalRecords = storesPage.totalCount ?? 0;

    this.ensureSelectedStoreExists(storeType);
  }

  private async ensureSelectedStoreExists(storeType: 'retailer' | 'origin' | 'destination'): Promise<void> {
    const selected =
      storeType === 'retailer' ? this.shipment.retailer :
        storeType === 'origin' ? this.shipment.originRetailer :
          this.shipment.destinationRetailer;

    if (!selected?.id) return;

    const list = this.storeData[storeType].list ?? [];
    const exists = list.some((x: any) => x?.id === selected.id);

    if (!exists) {
      const current = await firstValueFrom(this.retailersService.apiRetailersComboValueIdGet(selected.id))
      this.storeData[storeType].list = [current, ...list];
    }
  }

  async onLazyLoadStores(event: any) {
    await this.loadStores('retailer', event);
  }

  async onLazyLoadOriginStores(event: any) {
    await this.loadStores('origin', event);
  }

  async onLazyLoadDestinationStores(event: any) {
    await this.loadStores('destination', event);
  }

  async initForm() {
    this.shipmentForm.patchValue(
      {
        reference: this.shipment.name ?? null,
        retailer: this.shipment.retailerId ? [this.shipment.retailerId] : [],
        origin: this.shipment.originRetailerId ? [this.shipment.originRetailerId] : [],
        destination: this.shipment.destinationRetailerId ? [this.shipment.destinationRetailerId] : [],
        comment: this.shipment.comment ?? null
      },
      { emitEvent: false }
    );
  }

  async submitForm() {
    if (this.shipmentForm.valid) {

      const payload: ShipmentRequest = {
        name: this.shipmentForm.controls['reference'].value,
        originRetailerId: this.shipmentForm.controls['origin'].value?.[0],
        destinationRetailerId: this.shipmentForm.controls['destination'].value?.[0],
        retailerId: this.shipmentForm.controls['retailer'].value?.[0],
        comment: this.shipmentForm.controls['comment'].value
      };

      await firstValueFrom(this.shipmentsService.apiShipmentsUpdateIdPut(this.shipmentId, payload));

      this.router.navigate(['/shipments', this.shipmentId]);
    }
  }

}
